import React, { useState, useMemo, useRef, useEffect } from "react";
import { PRODUCTS_DATA } from "../data/products";
import ProductCard from "./ProductCard";
import styles from "./ProductsSection.module.css";

const PAGE_SIZE = 20;

const CATEGORY_MAP = {
  "All":                [],
  "Stationery":         ["Pencil Cases", "School Supplies", "Highlighters & Markers", "Diaries"],
  "Travel Accessories": ["Pouches", "Key Chains", "Bags", "Bottles", "Other Essentials"],
  "Kids Accessories":   ["Lunch Essentials", "Mugs & Bottles"],
  "Gifts & Toys":       [],
  "Gift Hampers":       ["Customisable Hamper"],
  "Seasonal":           [],
};

const CATEGORIES = Object.keys(CATEGORY_MAP);

function CategoryDropdown({ cat, activeCategory, activeSubCategory, onCategory, onSubCategory }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const subs = CATEGORY_MAP[cat];
  const isActive = activeCategory === cat || (subs.length > 0 && subs.includes(activeSubCategory) && activeCategory === cat);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (subs.length === 0) {
    return (
      <button
        className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ""}`}
        onClick={() => { onCategory(cat); }}
      >
        {cat}
      </button>
    );
  }

  return (
    <div className={styles.dropdownWrap} ref={ref}>
      <button
        className={`${styles.filterBtn} ${isActive ? styles.filterBtnActive : ""} ${styles.filterBtnDropdown}`}
        onClick={() => setOpen((o) => !o)}
      >
        {cat}
        <svg className={`${styles.arrow} ${open ? styles.arrowOpen : ""}`} viewBox="0 0 10 6" width="10" height="6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className={styles.dropdownMenu}>
          <button
            className={`${styles.dropdownItem} ${activeCategory === cat && activeSubCategory === "All" ? styles.dropdownItemActive : ""}`}
            onClick={() => { onCategory(cat); setOpen(false); }}
          >
            All
          </button>
          {subs.map((sub) => (
            <button
              key={sub}
              className={`${styles.dropdownItem} ${activeSubCategory === sub ? styles.dropdownItemActive : ""}`}
              onClick={() => { onSubCategory(sub, cat); setOpen(false); }}
            >
              {sub}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductsSection() {
  const [activeCategory, setActiveCategory]       = useState("All");
  const [activeSubCategory, setActiveSubCategory] = useState("All");
  const [visible, setVisible]                     = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    return PRODUCTS_DATA.filter((p) => {
      const catMatch = activeCategory === "All" || p.category === activeCategory;
      const subMatch = activeSubCategory === "All" || p.subCategory === activeSubCategory;
      return catMatch && subMatch;
    });
  }, [activeCategory, activeSubCategory]);

  const shown = filtered.slice(0, visible);

  const handleCategory = (cat) => {
    setActiveCategory(cat);
    setActiveSubCategory("All");
    setVisible(PAGE_SIZE);
  };

  const handleSubCategory = (sub, cat) => {
    setActiveCategory(cat);
    setActiveSubCategory(sub);
    setVisible(PAGE_SIZE);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>

        <div className={styles.header}>
          <p className={styles.label}>✦ OUR CATEGORIES</p>
          <h2 className={styles.title}>Shop Moon Berry Products</h2>
          <p className={styles.subtitle}>Cute, trendy & thoughtfully curated — all in one place</p>
        </div>

        <div className={styles.filters}>
          {CATEGORIES.map((cat) => (
            <CategoryDropdown
              key={cat}
              cat={cat}
              activeCategory={activeCategory}
              activeSubCategory={activeSubCategory}
              onCategory={handleCategory}
              onSubCategory={handleSubCategory}
            />
          ))}
        </div>

        <p className={styles.count}>{filtered.length} products</p>

        <div className={styles.grid}>
          {shown.map((product) => (
            <ProductCard key={product.code} product={product} />
          ))}
        </div>

        {visible < filtered.length && (
          <div className={styles.loadMore}>
            <button className={styles.loadMoreBtn} onClick={() => setVisible((v) => v + PAGE_SIZE)}>
              Load More ({filtered.length - visible} remaining)
            </button>
          </div>
        )}

      </div>
    </section>
  );
}
